import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { produce } from 'immer';
import {
  ChatState,
  ProgressPhase,
  InterviewData,
  ApplicationData,
  ChatMessage,
  InterviewSession,
  UIState,
  AnalysisData,
  RatingData,
  FeedbackData,
  InterviewEvent,
  Question,
  SkeletonVariant
} from '../types/interview';
import { v4 as uuidv4 } from 'uuid';
import { StoreApi } from 'zustand';
import { getUiCopy, localizeFrontendStatus } from '../i18n/copy';
// import { useWebSocketStore } from './websocketStore'; // Removed unused import

// Main interview state interface
interface InterviewState {
  // Core state
  chatState: ChatState;
  progressPhase: ProgressPhase;
  session: InterviewSession;
  ui: UIState;
  applicationData: ApplicationData;
  interviewData: InterviewData;
  messages: ChatMessage[];
  analysis: AnalysisData;
  rating: RatingData;
  feedback: FeedbackData;

  // New state for granular loading
  preparationStatus: 'idle' | 'fetching' | 'analyzing_resume' | 'analyzing_questions' | 'ready';

  // Actions
  updateSession: (updates: Partial<InterviewSession>) => void;
  updateUI: (updates: Partial<UIState>) => void;
  addMessage: (
    content: string,
    type: 'user' | 'ai' | 'status',
    isHtml?: boolean,
    isReferral?: boolean,
    level?: 'info' | 'warning' | 'error' | 'success',
    phase?: 'verification' | 'standard' | 'complete',
    isRatingRequest?: boolean,
    is_acknowledgment?: boolean,
    is_explanation?: boolean,
    is_guardrail?: boolean,
    guardrail_kind?: ChatMessage['guardrail_kind'],
    is_question?: boolean
  ) => void;
  clearMessages: () => void;
  updateAnalysis: (analysis: Partial<AnalysisData>) => void;
  updateRating: (rating: Partial<RatingData>) => void;
  updateFeedback: (feedback: Partial<FeedbackData>) => void;
  handleInterviewEvent: (event: InterviewEvent, data?: any) => void;
  resetInterview: () => void;
  setApplicationData: (data: ApplicationData) => void;
  setJobDescription: (description: string) => void;
  showJobDescription: () => void;
  hideJobDescription: () => void;
  startProcessing: (message: string) => void;
  stopProcessing: () => void;
  setThinking: (isThinking: boolean) => void;
  showSkeleton: (variant: SkeletonVariant) => void;
  hideSkeleton: () => void;
  setShowReferralForm: (show: boolean) => void;
  appendToLastAiMessage: (chunk: string) => void;
  setLoadingState: (message: string, progress: number) => void;
  setVisualLoadingProgress: (progress: number) => void;
  setPreparationStatus: (status: 'idle' | 'fetching' | 'analyzing_resume' | 'analyzing_questions' | 'ready') => void;

  // Computed states
  canSubmitInput: () => boolean;
  getBooleanQuestionInfo: () => { questionIndex: number; isActive: boolean } | null;
}

// Initial state matching the original useReducer structure
const initialState: Omit<InterviewState, 'canSubmitInput' | 'getBooleanQuestionInfo' | keyof ReturnType<typeof createActions>> = {
  chatState: 'WAITING_APP_ID',
  progressPhase: 'WAITING_APP_ID',
  preparationStatus: 'idle',
  session: {
    isConnected: false,
    connectionAttempts: 0,
    phase: 'verification',
    isFirstQuestion: true,
    hasStartedStandardPhase: false,
    interviewStartRequested: false,
    showVerificationHeader: true,
    showInputArea: false,
    inputPlaceholder: 'Enter Application ID...',
    buttonText: 'Start',
    isProcessing: false,
    showLoadingOverlay: false,
    isComplete: false,
    hasRated: false,
    currentRating: 0,
    hasSubmittedReferral: false,
    hasSubmittedFeedback: false,
    jobDescription: '',
    interviewLanguage: 'English',
    uiLocale: 'en',
    recognitionLocale: 'en-US',
    htmlLang: 'en',
  },
  ui: {
    inputValue: '',
    inputDisabled: false,
    inputPlaceholder: 'Enter Application ID...',
    sendButtonText: 'Send',
    sendButtonDisabled: false,
    showInputArea: false, // Changed to false to prevent flash of input on load
    showBooleanButtons: false,
    showVerificationHeader: false,
    showRatingStars: false,
    showReferralForm: false,
    showFeedbackForm: false,
    showLoadingIndicator: false,
    loadingMessage: '',
    loadingProgress: 0,
    visualLoadingProgress: 0,
    showOverlay: false,
    overlayMessage: '',
    booleanAnswerGiven: false,
    isThinking: false,
    currentSkeleton: null,
    showRating: false,
    showJobDescriptionModal: false,
  },
  applicationData: {
    application_id: '',
    job_id: '',
    applicant_id: '',
    applicant_email: '',
    opportunity_discussed_id: undefined
  },
  interviewData: {
    questions: [],
    answers: [],
    currentQuestionIndex: 0,
    totalQuestions: 0,
    interviewPhase: 'verification',
  },
  messages: [],
  analysis: {
    content: '',
    isReady: false,
  },
  rating: {
    rating: 0,
    timestamp: new Date(),
    hasSubmitted: false,
  },
  feedback: {
    feedback: '',
    timestamp: new Date(),
    hasSubmitted: false,
  }
};

// Helper function to derive UI state from chat state
const getUIStateForChatState = (chatState: ChatState, currentState: InterviewState): Partial<UIState> => {
    const copy = getUiCopy(currentState.session.uiLocale);
    switch (chatState) {
        case 'WAITING_APP_ID': return { inputPlaceholder: copy.enterApplicationId, sendButtonText: copy.send, sendButtonDisabled: true, showInputArea: false, showBooleanButtons: false, showVerificationHeader: false, showRatingStars: false, showReferralForm: false, showFeedbackForm: false, showLoadingIndicator: false, showOverlay: false, };
        case 'VALIDATING_ID':
          // Hide input to avoid accidental key presses during validation
          return {
            inputPlaceholder: copy.validating,
            sendButtonText: copy.validating,
            sendButtonDisabled: true,
            showInputArea: false,
            showLoadingIndicator: false, // Use the main overlay instead
            loadingMessage: copy.validatingApplicationId,
            showOverlay: true, // Ensure overlay is visible during validation
          };
        case 'INTERVIEW_STARTED':
          // Keep input hidden until the server signals that the first question is ready
          return {
            inputPlaceholder: copy.preparingInterviewPlaceholder,
            sendButtonText: copy.send,
            sendButtonDisabled: true,
            showInputArea: false,
            showLoadingIndicator: false,
            showOverlay: true,
            overlayMessage: copy.preparingInterview,
            loadingMessage: copy.preparingInterview,
            loadingProgress: 0, // Set initial progress
          };
        case 'VERIFICATION': return { inputPlaceholder: currentState.ui.showBooleanButtons ? '' : copy.yourResponse, sendButtonText: copy.send, sendButtonDisabled: currentState.ui.showBooleanButtons, showInputArea: !currentState.ui.showBooleanButtons, showBooleanButtons: currentState.ui.showBooleanButtons, showVerificationHeader: true, showOverlay: false, };
        case 'STANDARD': return { inputPlaceholder: copy.yourResponse, sendButtonText: copy.send, sendButtonDisabled: false, showInputArea: true, showBooleanButtons: false, showVerificationHeader: false, showOverlay: false, };
        case 'COMPLETE': return { inputPlaceholder: '', sendButtonText: copy.complete, sendButtonDisabled: true, showInputArea: false, showBooleanButtons: false, showVerificationHeader: false, showRatingStars: true, showOverlay: false, };
        case 'WAITING_RATING': return { showRatingStars: true, showInputArea: false, showBooleanButtons: false, };
        case 'WAITING_REFERRAL': return { showReferralForm: true, showRatingStars: false, showInputArea: false, };
        case 'WAITING_FEEDBACK': return { showFeedbackForm: true, showReferralForm: false, showInputArea: false, };
        case 'ERROR': return { inputPlaceholder: copy.errorOccurred, sendButtonText: copy.retry, sendButtonDisabled: false, showLoadingIndicator: false, showOverlay: false, };
        default: return {};
    }
};

// Reducer for handling complex interview events
const interviewEventReducer = (state: InterviewState, event: InterviewEvent, data?: any): Partial<InterviewState> => {
  const changes: Partial<InterviewState> & { session?: Partial<InterviewSession> } = {};

  switch (event) {
    case 'CONNECTION_ESTABLISHED':
      changes.session = { ...state.session, isConnected: true };
      return changes;

    case 'VALIDATION_SUCCESS':
      changes.chatState = 'VALIDATING_ID'; // Correctly transition the state
      changes.progressPhase = 'VALIDATING_ID';
      if (data) {
        changes.applicationData = data;
      }
      return changes;

    case 'VERIFICATION_STARTED':
      changes.progressPhase = 'VERIFICATION';
      changes.session = { ...state.session, phase: 'verification', showVerificationHeader: true };
      return changes;
    case 'STANDARD_PHASE_STARTED':
      changes.progressPhase = 'STANDARD';
      changes.session = { ...state.session, phase: 'standard', hasStartedStandardPhase: true, showVerificationHeader: false };
      changes.chatState = 'STANDARD';
      return changes;
    case 'QUESTION_RECEIVED': {
      const { text, is_boolean_question, is_first_question, question_index, phase, is_salary_question, total_questions } = data;
      const resolvedQuestionIndex = Number.isInteger(question_index)
        ? question_index
        : state.interviewData.currentQuestionIndex;
      const newQuestion: Question = {
        id: uuidv4(),
        text: text,
        type: is_boolean_question ? 'boolean' : 'text',
        index: resolvedQuestionIndex,
        phase: phase,
      };
      const shouldShowButtons = is_boolean_question === true;

      // Ensure the progress bar reflects the phase of the incoming question.
      // This is crucial for resuming interviews at the correct step.
      const newProgressPhase = phase === 'standard' ? 'STANDARD' :
                               phase === 'verification' ? 'VERIFICATION' :
                               state.progressPhase;

      return {
        progressPhase: newProgressPhase,
        interviewData: {
          ...state.interviewData,
          questions: [...state.interviewData.questions, newQuestion],
          currentQuestionIndex: resolvedQuestionIndex,
          totalQuestions: total_questions || state.interviewData.totalQuestions, // Update total questions from backend
        },
        session: { ...state.session, isFirstQuestion: is_first_question ?? false },
        ui: {
          ...state.ui,
          showBooleanButtons: shouldShowButtons,
          showInputArea: !shouldShowButtons,
          booleanQuestionIndex: shouldShowButtons ? resolvedQuestionIndex : undefined,
          booleanAnswerGiven: false,
          inputPlaceholder: getUiCopy(state.session.uiLocale).yourResponse,
          isSalaryQuestion: is_salary_question === true,
        },
      };
    }
    case 'ANSWER_SUBMITTED': return { session: { ...state.session, isProcessing: true } };
    case 'INTERVIEW_COMPLETED': return { chatState: 'COMPLETE', progressPhase: 'COMPLETE', session: { ...state.session, isComplete: true } };
    case 'RATING_SUBMITTED': return { chatState: 'WAITING_REFERRAL', session: { ...state.session, hasRated: true } };
    case 'REFERRAL_SUBMITTED': return { chatState: 'WAITING_FEEDBACK', session: { ...state.session, hasSubmittedReferral: true } };
    case 'FEEDBACK_SUBMITTED': return { chatState: 'FEEDBACK_SUBMITTED', session: { ...state.session, hasSubmittedFeedback: true } };
    case 'ERROR_OCCURRED': return { chatState: 'ERROR' };
    default: return {};
  }
};

// --- Store Definition ---
// Actions are separated to ensure their references are stable
const createActions = (set: StoreApi<InterviewState>['setState'], get: StoreApi<InterviewState>['getState']) => ({
  updateSession: (updates: Partial<InterviewSession>) => {
    if (process.env.NODE_ENV === 'development') {
      console.trace('updateSession called with', updates);
    }
    set((state) => {
      const session = { ...state.session, ...updates };
      if (!updates.uiLocale && !updates.interviewLanguage) {
        return { session };
      }
      const derived = getUIStateForChatState(state.chatState, { ...state, session });
      return {
        session,
        ui: {
          ...state.ui,
          ...(derived.inputPlaceholder !== undefined ? { inputPlaceholder: derived.inputPlaceholder } : {}),
          ...(derived.sendButtonText !== undefined ? { sendButtonText: derived.sendButtonText } : {}),
          ...(state.ui.overlayMessage
            ? { overlayMessage: localizeFrontendStatus(state.ui.overlayMessage, session.uiLocale) }
            : {}),
          ...(state.ui.loadingMessage
            ? { loadingMessage: localizeFrontendStatus(state.ui.loadingMessage, session.uiLocale) }
            : {}),
        },
      };
    });
  },
  updateUI: (updates: Partial<UIState>) => {
    if (process.env.NODE_ENV === 'development') {
      console.trace('updateUI called with', updates);
    }
    set((state) => ({ ui: { ...state.ui, ...updates } }));
  },
  addMessage: (
    content: string,
    type: 'user' | 'ai' | 'status',
    isHtml = false,
    isReferral = false,
    level: 'info' | 'warning' | 'error' | 'success' = 'info',
    phase?: 'verification' | 'standard' | 'complete',
    isRatingRequest = false,
    is_acknowledgment = false,
    is_explanation = false,
    is_guardrail = false,
    guardrail_kind?: ChatMessage['guardrail_kind'],
    is_question = false
  ) => {
    const newMessage: ChatMessage = {
      id: uuidv4(),
      content,
      type,
      timestamp: new Date(),
      is_html: isHtml,
      is_referral: isReferral,
      level,
      phase,
      isRatingRequest,
      is_acknowledgment,
      is_explanation,
      is_guardrail,
      guardrail_kind,
      is_question,
    };
    set(state => ({ messages: [...state.messages, newMessage] }));
  },
  clearMessages: () => set({ messages: [] }),
  updateAnalysis: (analysis: Partial<AnalysisData>) => set((state) => ({ analysis: { ...state.analysis, ...analysis } })),
  updateRating: (rating: Partial<RatingData>) => set((state) => ({ rating: { ...state.rating, ...rating } })),
  updateFeedback: (feedback: Partial<FeedbackData>) => set((state) => ({ feedback: { ...state.feedback, ...feedback } })),
  handleInterviewEvent: (event: InterviewEvent, data?: any) => {
    set(
      produce((state: InterviewState) => {
        const changes = interviewEventReducer(state, event, data);
        // Further logic to handle UI changes based on state...
        if (changes.chatState && changes.chatState !== state.chatState) {
          const uiChanges = getUIStateForChatState(changes.chatState, { ...state, ...changes } as InterviewState);
          changes.ui = { ...state.ui, ...uiChanges, ...(changes.ui || {}) };
        } else if (Object.keys(changes).length > 0) {
            // This case handles events that don't change chatState but might change other UI-relevant state
            const uiChanges = getUIStateForChatState(state.chatState, { ...state, ...changes } as InterviewState);
            changes.ui = { ...state.ui, ...uiChanges, ...(changes.ui || {}) };
        }
        Object.assign(state, changes);
      })
    );
  },
  resetInterview: () => {
    set(() => ({
      ...initialState,
      ui: {
        ...initialState.ui,
        loadingMessage: '',
        loadingProgress: 0,
        visualLoadingProgress: 0,
       },
      messages: [],
      session: {
        isConnected: false,
        connectionAttempts: 0,
        phase: 'verification',
        isFirstQuestion: true,
        hasStartedStandardPhase: false,
        interviewStartRequested: false,
        showVerificationHeader: true,
        showInputArea: false,
        inputPlaceholder: 'Enter Application ID...',
        buttonText: 'Start',
        isProcessing: false,
        showLoadingOverlay: false,
        isComplete: false,
        hasRated: false,
        currentRating: 0,
        hasSubmittedReferral: false,
        hasSubmittedFeedback: false,
        jobDescription: '',
        interviewLanguage: 'English',
        uiLocale: 'en',
        recognitionLocale: 'en-US',
        htmlLang: 'en',
      },
    }));
  },
  setApplicationData: (data: ApplicationData) => set({ applicationData: data }),
  setJobDescription: (description: string) => set(produce((draft: InterviewState) => {
    draft.session.jobDescription = description;
  })),
  showJobDescription: () => set(produce((draft: InterviewState) => {
    draft.ui.showJobDescriptionModal = true;
  })),
  hideJobDescription: () => set(produce((draft: InterviewState) => {
    draft.ui.showJobDescriptionModal = false;
  })),
  startProcessing: (message: string) => set(state => ({
    session: { ...state.session, isProcessing: true },
    ui: { ...state.ui, showLoadingIndicator: true, loadingMessage: message }
  })),
  stopProcessing: () => set(state => ({
    session: { ...state.session, isProcessing: false },
    ui: { ...state.ui, showLoadingIndicator: false, loadingMessage: '' }
  })),
  setThinking: (isThinking: boolean) => set(state => ({
    ui: { ...state.ui, isThinking },
  })),
  showSkeleton: (variant: SkeletonVariant) => set(state => ({
    ui: { ...state.ui, currentSkeleton: variant },
  })),
  hideSkeleton: () => set(state => ({
    ui: { ...state.ui, currentSkeleton: null },
  })),
  setShowReferralForm: (show: boolean) => set(state => ({
    ui: { ...state.ui, showReferralForm: show },
  })),
  appendToLastAiMessage: (chunk: string) => {
    set(state => {
      const msgs = [...state.messages];
      if (msgs.length === 0) {
        msgs.push({
          id: uuidv4(),
          content: chunk,
          type: 'ai',
          timestamp: new Date(),
        } as any);
      } else {
        const last = msgs[msgs.length - 1];
        if (last.type === 'ai' && !last.is_question) {
          const updated = { ...last, content: last.content + chunk } as any;
          msgs[msgs.length - 1] = updated;
        } else {
          msgs.push({
            id: uuidv4(),
            content: chunk,
            type: 'ai',
            timestamp: new Date(),
          } as any);
        }
      }
      return { messages: msgs } as Partial<InterviewState>;
    });
  },
  setLoadingState: (message: string, progress: number) => {
    set(produce((draft: InterviewState) => {
      draft.ui.loadingMessage = message;
      draft.ui.loadingProgress = progress;
    }));
  },
  setVisualLoadingProgress: (progress: number) => {
    set(produce((draft: InterviewState) => {
      draft.ui.visualLoadingProgress = progress;
    }));
  },
  setPreparationStatus: (status: 'idle' | 'fetching' | 'analyzing_resume' | 'analyzing_questions' | 'ready') => {
    set({ preparationStatus: status });
  },
});

// The store is created by combining initial state with actions
export const useInterviewStore = create<InterviewState>()(
  devtools(
    (set, get) => ({
      ...initialState,
      ...createActions(set, get),
    }),
    {
      name: 'interview-store',
    }
  )
);

// --- Selectors ---
// Selectors are functions that "select" and derive state, preventing unnecessary re-renders.
export const selectBooleanQuestionInfo = (state: InterviewState) => {
  const { ui } = state;
  const isActive = ui.showBooleanButtons && ui.booleanQuestionIndex !== undefined;
  if (!isActive) return null;
  return {
    questionIndex: ui.booleanQuestionIndex!,
    isActive: true,
  };
};

export const selectCanSubmitInput = (state: InterviewState) => {
  const { ui, session } = state;
  if (ui.inputDisabled || session.isProcessing || ui.sendButtonDisabled) {
    return false;
  }
  return ui.inputValue.trim().length > 0;
};
